import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.publishDate,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: '51吃瓜',
          },
          image: article.imageUrl,
          url: `https://51chigua.com/article/${slug}`,
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <div className="bg-background-100 border-b border-background-200/70">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <article className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-10">
            <header className="mb-8">
              <div className="flex items-center flex-wrap gap-2 mb-4">
                <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2.5 py-1 rounded-full">
                  {article.categoryName}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded-full">
                    #{tag}
                  </span>
                ))}
              </div>

              <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line text-xs"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-xs"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-xs"></i>
                  {article.readTime}分钟阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line text-xs"></i>
                  {article.viewCount > 10000 ? `${(article.viewCount / 10000).toFixed(1)}万次` : `${article.viewCount}次`}浏览
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-chat-3-line text-xs"></i>
                  {article.commentCount}条评论
                </span>
              </div>
            </header>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover object-top"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs text-secondary-600 bg-secondary-100 px-3 py-1.5 rounded-full cursor-pointer hover:bg-secondary-200 transition-colors"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 pb-14">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-5">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-bold text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-1">{rel.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}